import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { fromMillisecondsToHours, fromMillisecondsToMinutes, fromMillisecondsToSeconds } from "@obg/ngx.global";
import { coerceBoolean, PlatformService } from "@obg/ngx.global.evo";
import { floor, padStart } from "lodash-es";
import { Subscription, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { ProgressBarMode } from "../progress-bar/progress-bar.model";

@Component({
	selector: "obg-countdown-progress-bar",
	templateUrl: "./countdown-progress-bar.container.html",
	styleUrls: ["./countdown-progress-bar.container.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountdownProgressBarContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-countdown-progress-bar") hostCssClass = true;

	/**
	 * Time in milliseconds to end-up the countdown
	 */
	@Input() endsOn = 0;

	@Input() @coerceBoolean() hideHours = false;

	@Output() finish = new EventEmitter();

	timeRemaining = "";
	currentProgress = 0;
	readonly progressBarMode = ProgressBarMode.Countdown;
	private progressTimer$$: Subscription | undefined;

	constructor(
		private changeDetector: ChangeDetectorRef,
		private platformService: PlatformService
	) {}

	ngOnInit() {
		if (!this.platformService.isBrowser) {
			this.finish.emit();
			return;
		}

		const timeOut$ = timer(this.endsOn + 1000);
		this.progressTimer$$ = timer(0, 1000)
			.pipe(takeUntil(timeOut$))
			.subscribe({
				next: (secondsPassed) => {
					this.currentProgress = secondsPassed * 1000;
					this.timeRemaining = this.computeRemainingTime();
					this.changeDetector.markForCheck();
				},
				complete: () => this.finish.emit(),
			});
	}

	ngOnDestroy() {
		this.progressTimer$$?.unsubscribe();
	}

	private computeRemainingTime(): string {
		const timeRemaining = this.endsOn - this.currentProgress;

		const seconds = this.formatTimeUnit(fromMillisecondsToSeconds(timeRemaining) % 60);
		const minutes = this.formatTimeUnit(fromMillisecondsToMinutes(timeRemaining) % 60);
		const hours = this.formatTimeUnit(fromMillisecondsToHours(timeRemaining));

		return this.hideHours ? `${minutes}:${seconds}` : `${hours}:${minutes}:${seconds}`;
	}

	private formatTimeUnit(unit: number): string {
		return padStart(floor(unit).toString(), 2, "0");
	}
}
